// One park visit: the socket, the world store and the actions a player can ask for. The client
// only asks; every answer (ack or nack) comes from the worker.

import { KILL_RANGE_TILES, PRESETS } from '@wahala/config';
import { type TaskGame, TILE } from '@wahala/maps';
import {
  type ActionIntent,
  affordancesSchema,
  type ClientIntent,
  type PublicEvent,
  type QuickChatId,
  type ServerFrame,
} from '@wahala/protocol';
import {
  BUTTON_RADIUS,
  type PlayerView,
  REPAIR_RADIUS,
  REPORT_RADIUS,
  ROLE_DEFAULTS,
  ROLES_WITH_ABILITY,
  ROLES_WITH_TARGET,
  type Affordances as ServerAffordances,
  SHIELD_RANGE,
  type SpecialRole,
  STATION_RADIUS,
  STOPPED_SPEED,
  stopAt,
  type TaskOutcome,
  VENT_RADIUS,
} from '@wahala/simulation';
import { isFramed, vatar } from '@wahala/vatar-client';
import { siren } from '../lib/ambience';
import { api, type JoinTicket } from '../lib/api';
import { isPaused } from '../lib/pause';
import { sfx } from '../lib/sfx';
import { playHorn } from '../lib/sound';
import { NetClient, type NetLike, type NetOptions, type NetState } from '../net/client';
import { World } from '../store/world';
import { S } from '../strings';
import { stickOnMap } from './camera';
import { ChatGate } from './chatGate';
import { SirenWatch } from './sirenWatch';
import { nearestWithin } from './targets';

export interface Affordances {
  stop: { taskId: string; label: string; holdMs: number; game: TaskGame } | null;
  report: string | null;
  horn: boolean;
  /** Ms until the horn may call a meeting (the park-wide cooldown at the start and after each meeting). */
  hornReadyIn: number;
  knock: string | null;
  knockReadyIn: number;
  /** Agberos (on the road or spirits): a wahala may start now. */
  wahala: boolean;
  wahalaReadyIn: number;
  /** The repair station in reach for the wahala that is running. */
  fix: string | null;
  /** Agberos on the road: the shortcut in reach, and how long until one may be used again. */
  shortcut: string | null;
  shortcutReadyIn: number;
  /** May this player use the shortcuts at all (agberos, conductors; in hide and seek, drivers with escapes left)? */
  shortcuts: boolean;
  /** Special roles (GP-17): your role's button, if it has one and you may use it in your state. */
  ability: {
    role: SpecialRole;
    /** May it be pressed now (ready, not running, a target in reach when it needs one)? */
    ready: boolean;
    readyIn: number;
    /** Seconds of the role's cooldown, for the disc's ring. */
    total: number;
    /** Running now (a shadow's press then steps back into sight). */
    active: boolean;
    targetId: string | null;
  } | null;
  /**
   * Passengers: the stop you are at with someone to drop or load there. `drop` when a rider gets
   * off here; `ready` once the keke has stopped (nobody climbs into a moving keke).
   */
  ride: { stationId: string; drop: boolean; ready: boolean } | null;
}

let actionSeq = 0;
const newActionId = () => `${Date.now().toString(36)}${(++actionSeq).toString(36)}`.slice(-24);
const d2 = (a: { x: number; y: number }, b: { x: number; y: number }) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

export class ParkSession {
  readonly world = new World();
  readonly net: NetLike;
  /** A practice park (the tutorial): played in this page, nothing reaches our servers or Lagos Life. */
  readonly practice: boolean;
  readonly code: string;
  netState: NetState = 'connecting';
  closed: string | null = null;
  /** The joystick writes here; the 50 ms input step reads it. */
  stick = { dx: 0, dy: 0 };
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  /** The worker's chat limit, kept on this phone too: a line over it waits instead of being lost. */
  private readonly chatGate = new ChatGate(performance.now());
  private readonly sirens = new SirenWatch();

  constructor(
    t: JoinTicket,
    private readonly onClosed: (reason: string) => void,
    local?: { net: (o: NetOptions) => NetLike },
  ) {
    this.code = t.code;
    this.practice = local !== undefined;
    this.net = (local?.net ?? ((o: NetOptions) => new NetClient(o)))({
      roomId: t.roomId,
      workerUrl: t.workerUrl,
      ticket: t.ticket,
      onFrame: (f) => this.onFrame(f),
      onState: (s, why) => {
        this.netState = s;
        if (s === 'resuming') this.world.toast(S.errors.network ?? '', 'info', 2500);
        if (s === 'closed' && why) this.finish(why.code === 4409 ? 'replaced' : why.reason || 'generic');
        this.world.changed();
      },
      rejoin: async () => {
        // a stake park is entered only through its Lagos Life match; the api places it wherever it
        // lives now (another worker after its own died)
        const r = t.vatarMatch ? await api.bindStake(t.vatarMatch) : await api.joinPark(this.code);
        return { workerUrl: r.workerUrl, ticket: r.ticket };
      },
    });
  }

  private finish(reason: string): void {
    if (this.closed) return;
    this.closed = reason;
    this.cancelHold();
    this.onClosed(reason);
  }

  leave(): void {
    this.cancelHold();
    this.net.close();
    this.finish('left');
  }

  private onFrame(f: ServerFrame): void {
    let events: PublicEvent[] = [];
    this.world.apply(f, (e) => {
      events = e;
      this.onEvents(e);
    });
    // a wahala or an arrest nearby: the police siren, faint and far (lib/parkbed.ts)
    for (const s of this.sirens.frame(this.world.view, events)) siren(s.dist, s.pan);
  }

  private name(id: string): string {
    const v = this.world.view;
    if (v?.you.id === id) return v.you.name;
    return v?.players.find((p) => p.id === id)?.name ?? this.world.lobby?.members.find((m) => m.id === id)?.name ?? '';
  }

  /**
   * Was the keke reported a clamped one? The breakdown nearest the caller, as this phone last saw
   * the road (a guess for the meeting board's words only: nothing is decided by it).
   */
  private reportedClamp(by: string): boolean {
    const v = this.world.view;
    if (!v) return false;
    const at = v.you.id === by ? (this.world.predicted ?? v.you.pos) : v.players.find((p) => p.id === by)?.pos;
    let best: (typeof v.bodies)[number] | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const b of v.bodies) {
      const d = at ? d2(at, b.pos) : 0;
      if (d < bestD) {
        best = b;
        bestD = d;
      }
    }
    return best?.clamp === true;
  }

  /** Race events go to the race's own module, loaded once (in order: each waits on the one load). */
  private raceEvents: Promise<typeof import('./race-events')> | null = null;
  private raceEvent(e: PublicEvent): void {
    this.raceEvents ??= import('./race-events');
    void this.raceEvents.then((m) => m.raceEvent(e, this.world)).catch(() => {});
  }

  private onEvents(events: PublicEvent[]): void {
    for (const e of events) {
      if (e.t === 'match.ended') {
        if (!this.practice) void this.sendScore();
        const v = this.world.view;
        // a race is won over the line: first place wins, everyone else loses
        const mine = v?.you.role === 'impostor' ? 'impostor' : 'crew';
        if (v?.race ? v.race.place === 1 : e.team === mine) sfx.win();
        else sfx.lose();
      } else if (e.t === 'meeting.called') {
        // the meeting board itself says who called it; a horn call sounds the caller's own horn,
        // a reported breakdown the conductor's whistle
        this.cancelHold();
        this.world.impounded = e.reason === 'report' && this.reportedClamp(e.by);
        if (e.reason === 'button') playHorn(this.world.looks.get(e.by)?.horn);
        else sfx.whistle();
      } else if (e.t === 'vote.cast') {
        sfx.vote();
      } else if (e.t === 'vote.result') {
        if (e.ejected) sfx.owa();
        else sfx.nobody();
      } else if (e.t === 'sabotage.started') {
        // the wahala banner itself is the news (it shakes in with the alarm): no second card
        sfx.alarm();
      } else if (e.t === 'sabotage.progress') {
        // the banner counts the parts fixed
        sfx.half();
      } else if (e.t === 'sabotage.fixed') {
        sfx.fixed();
        this.world.callout({ title: S.wahala.fixed, tone: 'done', icon: 'check' });
      } else if (e.t === 'power.picked') {
        sfx.power();
        const name = S.powers.names[e.power] ?? S.powers.pickup;
        this.world.callout({ title: S.callout.picked(name), tone: 'danfo', icon: e.power });
      } else if (e.t === 'power.used') {
        // your own small news: a pop beside the purse, not a card over the road
        const name = S.powers.names[e.power] ?? S.powers.pickup;
        this.world.blip(S.callout.powerOn(name), 'danfo');
      } else if (e.t === 'ability.used') {
        const title = e.active ? (S.specials.on[e.role] ?? '') : S.specials.off;
        if (title) this.world.blip(title, 'danfo');
      } else if (e.t === 'role.alarm') {
        sfx.alarm();
        this.world.callout({ title: S.specials.alarm, tone: 'wahala', icon: 'horn' }, 4000, 2);
      } else if (e.t === 'decoy.reported') {
        this.world.callout({ title: S.callout.fake, tone: 'info', icon: 'decoy' });
      } else if (e.t === 'task.completed') {
        // the route bar moves and the stop's ring fills: a ding and a pop are enough
        sfx.ding();
        this.world.blip(S.ride.stopDone, 'done');
      } else if (e.t === 'player.departed') {
        const n = this.name(e.playerId);
        if (n) this.world.callout({ title: S.hud.departed(n), tone: 'info', icon: 'shout' }, 3000, 0);
      } else if (e.t === 'ride.paid') {
        sfx.coins();
        // a stake park never shows coins mid-round: there it counts drops
        this.world.blip(this.world.stakePark ? S.ride.dropped(e.riders) : S.ride.paid(e.coins), 'danfo');
      } else if (e.t === 'ride.boarded') {
        sfx.board();
        this.world.blip(S.ride.boarded(e.riders), 'done');
      } else if (e.t === 'ride.lost') {
        this.world.blip(S.ride.lost, 'wahala');
      } else if (e.t === 'boys.settled') {
        sfx.coins();
        this.world.blip(S.boys.settled(e.coins), 'wahala');
      } else if (e.t === 'boys.sent') {
        this.world.callout({ title: S.boys.sent, tone: 'danfo', icon: 'areaboy' });
      } else if (e.t === 'purse.banked') {
        sfx.ding();
        this.world.blip(S.boys.banked(e.coins), 'done');
      } else if (e.t.startsWith('race.')) {
        this.raceEvent(e);
      } else if (e.t === 'arrest.ended') {
        // to the two in it: the driver hears how Bribe or Bail went, the VIO what they got
        const officer = this.world.view?.you.role === 'impostor';
        if (officer) {
          this.world.blip(S.arrest.officerTitle[e.outcome] ?? '', e.outcome === 'yard' ? 'danfo' : 'wahala');
        } else {
          if (e.outcome === 'yard') sfx.cough();
          else if (e.outcome === 'lawyer') sfx.fixed();
          else sfx.coins();
          this.world.callout(
            {
              title: S.arrest.outcome(e.outcome, e.undone ?? 0),
              tone: e.outcome === 'lawyer' ? 'done' : 'wahala',
              icon: e.outcome === 'lawyer' ? 'check' : 'knock',
            },
            5000,
            2,
          );
        }
      } else if (e.t === 'breakout.cut') {
        sfx.half();
        this.world.blip(S.arrest.cutStage(e.stage, e.of), 'danfo');
      } else if (e.t === 'breakout.freed') {
        sfx.fixed();
        this.world.callout({ title: S.arrest.freed(e.count), tone: 'done', icon: 'check' });
      } else if (e.t === 'boys.latched') {
        sfx.crash();
      } else if (e.t === 'boys.gone') {
        if (!e.paid) this.world.blip(S.levy.gone, 'done');
      } else if (e.t === 'crash') {
        sfx.crash();
        this.world.blip(S.crash.bang, 'wahala');
      }
    }
  }

  /**
   * After a round, hand our server's count of rounds won to the Lagos Life board. The persist
   * worker applies the round a moment after it ends, so ask twice. Inside the frame only.
   */
  private async sendScore(): Promise<void> {
    if (!isFramed()) return;
    for (const wait of [4_000, 10_000]) {
      await new Promise((r) => setTimeout(r, wait));
      try {
        const { score } = await api.stats();
        if (score > 0) await vatar.submitScore(score);
        return;
      } catch {
        // too soon, rate limited, or offline: try once more, then leave it to the next round
      }
    }
  }

  // ------------------------------------------------------------ asking

  act(intent: ActionIntent): string {
    const actionId = newActionId();
    this.net.send({ ...intent, actionId } as ClientIntent);
    return actionId;
  }

  /** Send a typed line. False when it did not go (empty, or the chat limit says wait). */
  chat(text: string): boolean {
    const t = text.trim();
    if (!t || !this.chatGate.take(performance.now())) return false;
    this.net.send({ t: 'chat', text: t.slice(0, 140) });
    return true;
  }

  /** A quick-chat phrase: only its id goes (BE-13). The same limit as typed lines. */
  quickChat(phrase: QuickChatId): boolean {
    if (!this.chatGate.take(performance.now())) return false;
    this.net.send({ t: 'chat.quick', phrase });
    return true;
  }

  /** Milliseconds until the next chat line may go (0 when it may go now): Send counts it down. */
  chatWait(): number {
    return this.chatGate.waitMs(performance.now());
  }

  /**
   * Milliseconds left of the start hold: the room starts the match clock ahead, so the view's time
   * stands still past the server's own until the round begins. 0 once it runs (or between rounds).
   */
  holdLeft(): number {
    const v = this.world.view;
    if (!v || v.phase !== 'PLAYING') return 0;
    // capped at the longest hold a config allows, so a bad clock estimate never freezes the thumb
    return Math.min(10_000, Math.max(0, v.now - this.net.serverNow()));
  }

  /** Called every 50 ms by the renderer's input step. */
  inputTick = (): void => {
    // paused under a Lagos Life sheet: the keke stops (a thumb left on the stick doesn't drive); and
    // in the start hold nothing drives, so the predicted keke never runs ahead of the server's
    const held = !isPaused() && this.holdLeft() === 0;
    // in a race the road turns with my keke, so the stick steers as the screen shows it
    const yaw = this.world.view?.race ? this.world.chaseYaw : null;
    const s = held ? (yaw !== null ? stickOnMap(this.stick.dx, this.stick.dy, yaw) : this.stick) : { dx: 0, dy: 0 };
    const i = this.world.stepInput(s.dx, s.dy);
    if (i) this.net.send({ t: 'input', n: i.n, dx: i.dx, dy: i.dy, ...(i.b ? { b: true } : {}) });
    const busy = this.world.holding?.taskId ?? this.world.game?.taskId;
    if (busy && this.world.predicted) {
      const v = this.world.view;
      const task = v?.you.tasks.find((t) => t.id === busy);
      const st = task && this.world.map.stations.find((s) => s.id === task.stationId);
      if (st && d2(this.world.predicted, st.pos) > STATION_RADIUS * STATION_RADIUS) this.cancelHold();
    }
  };

  /** The last `affordances` the worker sent, and what the wire's strict schema made of it. */
  private canRaw: unknown;
  private canRead: ServerAffordances | null = null;

  /**
   * The worker's own word on my buttons (FE-18), read through the protocol's strict schema once per
   * change (a delta that leaves them alone keeps the same object). Null from a worker that sends
   * none, or anything the schema refuses: then the phone's own reckoning stands alone.
   */
  serverAffordances(): ServerAffordances | null {
    const raw = (this.world.view as { affordances?: unknown } | null)?.affordances;
    if (raw !== this.canRaw) {
      this.canRaw = raw;
      const r = raw === undefined ? null : affordancesSchema.safeParse(raw);
      this.canRead = r?.success ? r.data : null;
    }
    return this.canRead;
  }

  /**
   * What may light up. Only for the buttons: the worker decides every one of these again. The
   * worker's affordances say which buttons belong on the road, their cooldowns and what a press
   * would be on from where it has me; the phone's predicted position only adds reach, so a disc
   * lights the moment my keke rolls into range rather than a snapshot later.
   */
  affordances(): Affordances {
    const v = this.world.view;
    const local = this.predictedAffordances();
    const can = v?.phase === 'PLAYING' ? this.serverAffordances() : null;
    if (!v || !can) return local.out;
    const out = local.out;
    const now = this.net.serverNow();
    const left = (at: number) => Math.max(0, at - now);
    // a stop: mine in reach as predicted, or the one the worker has me at
    if (!can.task.may) out.stop = null;
    else if (!out.stop && can.task.id) {
      const t = v.you.tasks.find((x) => x.id === can.task.id && !x.done && x.readyAt <= now);
      if (t) out.stop = { taskId: t.id, label: t.label, holdMs: t.holdMs, game: t.game ?? 'hold' };
    }
    out.report = can.report.may ? (out.report ?? can.report.id) : null;
    out.fix = can.fix.may ? (out.fix ?? can.fix.id) : null;
    out.hornReadyIn = left(can.button.at);
    out.horn = can.button.may && out.hornReadyIn === 0 && (local.nearHorn || can.button.now);
    out.knockReadyIn = left(can.kill.at);
    out.knock = can.kill.may ? (out.knock ?? can.kill.id) : null;
    out.wahalaReadyIn = left(can.sabotage.at);
    out.wahala = can.sabotage.may && out.wahalaReadyIn === 0;
    if (!v.you.vent) {
      out.shortcuts = can.vent.may;
      out.shortcutReadyIn = can.vent.may ? left(can.vent.at) : 0;
      out.shortcut = can.vent.may ? (out.shortcut ?? can.vent.id) : null;
    }
    // passengers: where this phone has itself pulled up, lit when it has stopped or the worker says so
    if (!can.ride.may) out.ride = null;
    else if (out.ride && can.ride.now && can.ride.id === out.ride.stationId) out.ride.ready = true;
    return out;
  }

  /** The stop this phone has pulled up at, if a rider gets off there or people wait and a seat is free. */
  private rideNow(v: PlayerView, me: { x: number; y: number }): Affordances['ride'] {
    const r = v.you.ride;
    if (!r || v.you.vent !== null) return null;
    const st = stopAt(this.world.map, me);
    if (!st) return null;
    const drop = r.aboard.some((x) => x.to === st.id);
    const waiting = v.waiting?.find((w) => w.stationId === st.id)?.riders ?? 0;
    if (!drop && !(waiting > 0 && r.aboard.length < r.seats)) return null;
    const vel = this.world.predictedVel;
    return { stationId: st.id, drop, ready: vel.x * vel.x + vel.y * vel.y <= STOPPED_SPEED * STOPPED_SPEED };
  }

  /** The phone's own reckoning from its predicted position: instant, and the fallback. */
  private predictedAffordances(): { out: Affordances; nearHorn: boolean } {
    const v = this.world.view;
    const none: Affordances = {
      stop: null,
      report: null,
      horn: false,
      hornReadyIn: 0,
      knock: null,
      knockReadyIn: 0,
      wahala: false,
      wahalaReadyIn: 0,
      fix: null,
      shortcut: null,
      shortcutReadyIn: 0,
      shortcuts: false,
      ability: null,
      ride: null,
    };
    if (v?.phase !== 'PLAYING') return { out: none, nearHorn: false };
    // hide and seek: no horn, reports or wahala; the shortcuts are the drivers' escapes
    const hide = v.hide;
    const me = this.world.predicted ?? v.you.pos;
    const map = this.world.map;
    const out = { ...none };
    // every button points at the nearest thing in range, never just the first in the list
    const stationPos = (t: (typeof v.you.tasks)[number]) => map.stations.find((s) => s.id === t.stationId)?.pos;
    const task = nearestWithin(
      me,
      // a later stage still waiting (the oil draining) does not light the disc yet
      v.you.tasks.filter((t) => !t.done && t.readyAt <= this.net.serverNow()),
      stationPos,
      STATION_RADIUS,
    );
    if (task) out.stop = { taskId: task.id, label: task.label, holdMs: task.holdMs, game: task.game ?? 'hold' };
    if (v.you.role === 'impostor' && !hide) {
      out.wahalaReadyIn = Math.max(0, (v.you.sabotageReadyAt ?? 0) - this.net.serverNow());
      out.wahala = !v.sabotage && out.wahalaReadyIn === 0;
    }
    out.ability = this.abilityNow(v, me);
    if (!v.you.alive) return { out, nearHorn: false };
    out.ride = this.rideNow(v, me);
    if (v.sabotage) {
      const fixes = map.fixes[v.sabotage.kind].flatMap((id) => map.stations.filter((st) => st.id === id));
      out.fix = nearestWithin(me, fixes, (st) => st.pos, REPAIR_RADIUS)?.id ?? null;
    }
    out.report = hide ? null : (nearestWithin(me, v.bodies, (b) => b.pos, REPORT_RADIUS)?.id ?? null);
    const button = map.stations.find((s) => s.kind === 'button');
    out.hornReadyIn = Math.max(0, v.hornReadyAt - this.net.serverNow());
    // a critical wahala (go-slow, fuel) silences the horn
    const critical = v.sabotage?.kind === 'goslow' || v.sabotage?.kind === 'fuel';
    const nearHorn = Boolean(button && d2(me, button.pos) <= BUTTON_RADIUS * BUTTON_RADIUS);
    out.horn = !hide && nearHorn && !critical && v.you.meetingsLeft > 0 && out.hornReadyIn === 0;
    if (v.you.role === 'impostor') {
      const range = KILL_RANGE_TILES[PRESETS[this.world.preset].killRange] * TILE;
      out.knockReadyIn = Math.max(0, (v.you.killReadyAt ?? 0) - this.net.serverNow());
      const drivers = v.players.filter((p) => p.alive && p.role !== 'impostor' && p.id !== v.you.id);
      out.knock = nearestWithin(me, drivers, (p) => p.pos, range)?.id ?? null;
    }
    // a conductor rides the shortcuts while rides are left (and climbs out of the one they are in)
    const conductor = v.you.special === 'engineer' && (v.you.vent !== null || (v.you.ability?.charges ?? 0) > 0);
    out.shortcuts = hide
      ? v.you.role === 'crew' && (v.you.vent !== null || hide.escapes > 0)
      : v.you.role === 'impostor' || conductor;
    if (out.shortcuts) {
      out.shortcutReadyIn = Math.max(0, (hide ? hide.ventReadyAt : (v.you.ventReadyAt ?? 0)) - this.net.serverNow());
      out.shortcut = v.you.vent ? null : (nearestWithin(me, map.vents, (x) => x.pos, VENT_RADIUS)?.id ?? null);
    }
    return { out, nearHorn };
  }

  /**
   * Your role's button: the mechanic and the shadow just press it; the spotter and the copy-copy
   * point at the nearest keke this phone can see; the area boy, a spirit, at the nearest living
   * keke within reach. The worker checks it all again.
   */
  private abilityNow(v: PlayerView, me: { x: number; y: number }): Affordances['ability'] {
    const role = v.you.special;
    const a = v.you.ability;
    if (!role || !a || !ROLES_WITH_ABILITY.includes(role) || v.you.vent !== null) return null;
    if (role === 'guardian' ? v.you.alive : !v.you.alive) return null;
    const now = this.net.serverNow();
    const active = a.activeUntil > now;
    const readyIn = Math.max(0, a.cooldownUntil - now);
    let targetId: string | null = null;
    if (ROLES_WITH_TARGET.includes(role)) {
      const others = v.players.filter((p) => p.alive && !p.departed && p.pos && p.id !== v.you.id);
      const reach = role === 'guardian' ? SHIELD_RANGE : Number.POSITIVE_INFINITY;
      targetId = nearestWithin(me, others, (p) => p.pos as { x: number; y: number }, reach)?.id ?? null;
    }
    const spent = a.charges !== null && a.charges <= 0;
    const ready =
      role === 'phantom' && active
        ? true
        : !active && readyIn === 0 && !spent && (targetId !== null || !ROLES_WITH_TARGET.includes(role));
    return { role, ready, readyIn, total: ROLE_DEFAULTS[role].cooldownMs / 1000, active, targetId };
  }

  /** Start (or resume) a stop: ask the worker, hold for the stop's time, then ask it to finish. */
  startStop(a: NonNullable<Affordances['stop']>): void {
    const v = this.world.view;
    const open = v?.you.session;
    if (a.game !== 'hold') {
      // a game: open the stop on the worker (it deals the game's secret), then show the game
      const show = () => {
        this.world.game = { taskId: a.taskId, game: a.game as Exclude<TaskGame, 'hold'>, label: a.label, tries: 0 };
        this.world.changed();
      };
      if (open && open.taskId === a.taskId) {
        show();
        return;
      }
      const id = this.act({ t: 'task.interact', taskId: a.taskId });
      this.waitFor(id, (reason) => {
        if (reason === null || reason === 'SESSION_OPEN') show();
      });
      return;
    }
    const begin = (startedLocal: number) => {
      this.world.holding = {
        taskId: a.taskId,
        label: a.label,
        startedAt: startedLocal,
        until: startedLocal + a.holdMs,
      };
      this.world.changed();
      this.cancelHoldTimer();
      this.holdTimer = setTimeout(
        () => this.completeStop(a.taskId),
        Math.max(0, startedLocal + a.holdMs - Date.now()) + 150,
      );
    };
    if (open && open.taskId === a.taskId) {
      begin(open.startedAt - this.net.clockOffset);
      return;
    }
    const id = this.act({ t: 'task.interact', taskId: a.taskId });
    this.waitFor(id, (reason) => {
      if (reason === null) begin(Date.now());
      else if (reason === 'SESSION_OPEN') {
        const s = this.world.view?.you.session;
        begin(s ? s.startedAt - this.net.clockOffset : Date.now());
      }
    });
  }

  private completeStop(taskId: string): void {
    if (this.world.holding?.taskId !== taskId) return;
    const id = this.act({ t: 'task.complete', taskId });
    this.waitFor(id, (reason) => {
      this.world.holding = null;
      if (reason === 'TOO_SOON') {
        // the clock estimate was early: try once more shortly
        this.holdTimer = setTimeout(() => this.completeStop(taskId), 400);
      }
      this.world.changed();
    });
  }

  /** Hand the game's answer to the worker; a miss keeps the game open for another try. */
  submitGame(outcome: TaskOutcome): void {
    const g = this.world.game;
    if (!g) return;
    const id = this.act({ t: 'task.complete', taskId: g.taskId, outcome });
    this.waitFor(id, (reason) => {
      const now = this.world.game;
      if (!now || now.taskId !== g.taskId) return;
      if (reason === 'BAD_OUTCOME') this.world.game = { ...now, tries: now.tries + 1 };
      else if (reason === 'TOO_SOON') setTimeout(() => this.submitGame(outcome), 300);
      else this.world.game = null; // done (the callout says so), or the stop is gone
      this.world.changed();
    });
  }

  cancelHold(): void {
    if (this.world.game) {
      this.world.game = null;
      this.world.changed();
    }
    this.cancelHoldTimer();
    if (this.world.holding) {
      this.world.holding = null;
      this.world.changed();
    }
  }

  private cancelHoldTimer(): void {
    if (this.holdTimer) clearTimeout(this.holdTimer);
    this.holdTimer = null;
  }

  /** Call back once the worker answers this action (or give up after 5 s). */
  waitFor(actionId: string, cb: (reason: string | null) => void): void {
    const start = Date.now();
    const check = () => {
      const a = this.world.answers.get(actionId);
      if (a !== undefined) {
        this.world.answers.delete(actionId);
        cb(a);
      } else if (Date.now() - start < 5_000) setTimeout(check, 30);
    };
    check();
  }

  view(): PlayerView | null {
    return this.world.view;
  }
}
